import { useQuery, UseQueryResult, useQueryClient } from 'react-query';
import { ViewApI } from 'api/views';
import { View, ViewType } from 'api/views/types';
import { load } from 'components/molecules/views/core';
import { ViewTransformer } from 'components/molecules/views/transformer';
import { Views } from 'components/molecules/views/types';
import { SourceType } from 'components/organisms/tasks/fields';
import {
  ApiTasksViewDefinition,
  GroupedFilterViewDefinition,
  TasksViewDefinition,
} from '../components/organisms/tasks/views/types';

const TASK_VIEWS_QUERY_KEY = 'task-views';

const PROJECT_TASK_VIEWS_QUERY_KEY = 'project-task-views';
const ALL_TASK_VIEWS_QUERY_KEY = 'all-task-views';

interface TaskOptions {
  enabled: boolean;
  viewTransformer: ViewTransformer<ApiTasksViewDefinition, TasksViewDefinition>;
  projectId?: string;
}

const removeProjectFieldsInProjectTaskView = (
  view: View<ApiTasksViewDefinition>[],
): View<ApiTasksViewDefinition>[] => {
  return view.map((def) => ({
    ...def,
    viewDefinition: {
      group: def.viewDefinition.group,
      filter: def.viewDefinition.filter
        ? {
            match: def.viewDefinition.filter.match,
            customFields: def.viewDefinition.filter.customFields?.filter(
              (field) => field.sourceType !== SourceType.Project,
            ),
            nativeFields: def.viewDefinition.filter.nativeFields?.filter(
              (field) => field.sourceType !== SourceType.Project,
            ),
          }
        : undefined,
      columnOrder: def.viewDefinition.columnOrder,
    },
  }));
};

export const useTaskViewsQuery = ({
  enabled,
  viewTransformer,
  projectId,
}: TaskOptions): UseQueryResult<Views<TasksViewDefinition>> => {
  return useQuery({
    enabled,
    retry: false,
    refetchOnWindowFocus: false,
    queryKey: projectId
      ? [PROJECT_TASK_VIEWS_QUERY_KEY, projectId]
      : TASK_VIEWS_QUERY_KEY,
    queryFn: async () => {
      const data =
        projectId !== undefined
          ? await ViewApI.getAllProjectViews<ApiTasksViewDefinition>(projectId)
          : await ViewApI.getAllViews<ApiTasksViewDefinition>(
              ViewType.AllTasks,
            );
      const result = removeProjectFieldsInProjectTaskView(data);

      if (Array.isArray(result)) {
        return load(result.map(viewTransformer.from));
      }

      return result;
    },
  });
};

interface AllTaskOptions {
  enabled: boolean;
  viewTransformer: ViewTransformer<
    ApiTasksViewDefinition,
    GroupedFilterViewDefinition
  >;
}

export const useAllTaskViewsQuery = ({
  enabled,
  viewTransformer,
}: AllTaskOptions): UseQueryResult<Views<GroupedFilterViewDefinition>> => {
  return useQuery({
    enabled,
    retry: false,
    refetchOnWindowFocus: false,
    queryKey: [ALL_TASK_VIEWS_QUERY_KEY],
    queryFn: async () => {
      const data = await ViewApI.getAllViews<ApiTasksViewDefinition>(
        ViewType.AllTasks,
      );

      if (Array.isArray(data)) {
        return load(data.map(viewTransformer.from));
      }

      return data;
    },
  });
};

export const useUpdateTaskViewMutation = (): any => {
  const queryClient = useQueryClient();
  const onUpdate = async (data) => {
    queryClient.invalidateQueries(ALL_TASK_VIEWS_QUERY_KEY);
    queryClient.invalidateQueries(TASK_VIEWS_QUERY_KEY);
    await queryClient.setQueryData(ALL_TASK_VIEWS_QUERY_KEY, data);
    await queryClient.setQueryData(TASK_VIEWS_QUERY_KEY, data);
  };

  return { onUpdate };
};

export const useUpdateProjectTaskViewMutation = (projectId: string): any => {
  const queryClient = useQueryClient();
  const onUpdate = async (data: Views<TasksViewDefinition>) => {
    queryClient.invalidateQueries(TASK_VIEWS_QUERY_KEY);
    queryClient.invalidateQueries(ALL_TASK_VIEWS_QUERY_KEY);
    await queryClient.setQueryData(
      [PROJECT_TASK_VIEWS_QUERY_KEY, projectId],
      data,
    );
  };

  return { onUpdate };
};
